import { PlatformVacancyCompanyCandidateListOrderFields } from "@generated/graphql";
import { Stack } from "@mui/material";
import DownloadFileButton from "components/buttons/DownloadFileButton";
import dayjs from "dayjs";
import { ColumnConfig, RouteEnum } from "layout/TableConstructor/types";
import {
  VacancyCompanyCandidateFilter,
  VacancyCompanyCandidateItemWithAssessmentCV,
  VacancyCompanyCandidatesRefetch,
} from "pages/Vacancies/view/types";
import DeleteCandidateButton from "../../DeleteCandidateButton";

const getCriterionMatch = (data: VacancyCompanyCandidateItemWithAssessmentCV, index: number) => {
  return data.currentCvCheck?.assessmentCV?.criterionMatch?.[index];
};

type GetColumnConfigParams = {
  setVacancyCompanyCandidatesRefetch: VacancyCompanyCandidatesRefetch;
};

const getColumnConfig = ({
  setVacancyCompanyCandidatesRefetch,
}: GetColumnConfigParams): ColumnConfig<
  VacancyCompanyCandidateItemWithAssessmentCV,
  VacancyCompanyCandidateFilter,
  PlatformVacancyCompanyCandidateListOrderFields,
  undefined,
  RouteEnum.VacancyCandidate
>[] => [
  {
    name: "numericId",
    title: "ID кандидата",
    filterName: "numericId",
    sortField: PlatformVacancyCompanyCandidateListOrderFields.CompanyCandidateNumericId,
    render: (data) => data.companyCandidate.numericId,
  },
  {
    name: "createdAt",
    title: "Дата создания",
    filterName: "createdAt",
    sortField: PlatformVacancyCompanyCandidateListOrderFields.CompanyCandidateCreatedAt,
    render: (data) => (data.createdAt ? dayjs(data.createdAt).format("DD.MM.YYYY") : "-"),
  },
  {
    name: "telegramUsername",
    title: "Ник Telegram",
    filterName: "telegramUsername",
    sortField: PlatformVacancyCompanyCandidateListOrderFields.CompanyCandidateCandidateUsernameTelegramUsername,
    render: (data) => data.companyCandidate.candidateUsername?.telegramUsername,
  },
  {
    name: "login",
    title: "Логин",
    filterName: "login",
    sortField: PlatformVacancyCompanyCandidateListOrderFields.CompanyCandidateCandidateUsernameLogin,
    render: (data) => data.companyCandidate.candidateUsername?.login || "-",
  },
  {
    name: "fullName",
    title: "ФИО кандидата",
    filterName: "fullName",
    sortField: PlatformVacancyCompanyCandidateListOrderFields.CompanyCandidateFullName,
    render: (data) => data.companyCandidate.fullName || "-",
  },
  {
    name: "isCVFile",
    title: "Файл резюме",
    filterName: "isCVFile",
    sortField: PlatformVacancyCompanyCandidateListOrderFields.CompanyCandidateCvFileId,
    render: (data) => (data.companyCandidate.cvFileId ? "Да" : "Нет"),
  },
  {
    name: "cvCheckScore",
    title: "Соответствие вакансии",
    filterName: "cvCheckScore",
    sortField: PlatformVacancyCompanyCandidateListOrderFields.CvChecksScore,
    render: (data) => data.currentCvCheck?.score ?? "-",
  },
  {
    name: "action",
    title: "Действие",
    render: (data) => {
      const cvReportUrl = data.currentCvCheck?.reportFile?.url;

      return (
        <Stack
          direction='row'
          sx={{ justifyContent: "space-between", alignItems: "center" }}>
          {cvReportUrl && (
            <DownloadFileButton
              href={cvReportUrl!}
              tooltipText='Отчет по CV'
            />
          )}
          <DeleteCandidateButton
            candidateId={data.id}
            setVacancyCompanyCandidatesRefetch={setVacancyCompanyCandidatesRefetch}
          />
        </Stack>
      );
    },
  },
  {
    name: "totalExperienceYears",
    title: "Стаж",
    filterName: "totalExperienceYears",
    render: (data) => {
      return data.currentCvCheck?.assessmentCV?.totalExperienceYears ?? "-";
    },
  },
  {
    name: "criterionMatch_experience",
    title: 'Критерий "Опыт"',
    filterName: "criterionMatch_experience",
    render: (data) => getCriterionMatch(data, 0)?.score ?? "-",
  },
  {
    name: "criteria_experience_text",
    title: 'Критерий "Опыт" (комментарий)',
    render: (data) => getCriterionMatch(data, 0)?.explanation ?? "-",
  },
  {
    name: "criterionMatch_relevance",
    title: 'Критерий "Релевантность"',
    filterName: "criterionMatch_relevance",
    render: (data) => getCriterionMatch(data, 1)?.score ?? "-",
  },
  {
    name: "criteria_relevance_text",
    title: 'Критерий "Релевантность" (комментарий)',
    render: (data) => getCriterionMatch(data, 1)?.explanation ?? "-",
  },
  {
    name: "criterionMatch_skills",
    title: 'Критерий "Навыки и сертификаты"',
    filterName: "criterionMatch_skills",
    render: (data) => getCriterionMatch(data, 2)?.score ?? "-",
  },
  {
    name: "criteria_skills_text",
    title: 'Критерий "Навыки и сертификаты" (комментарий)',
    render: (data) => getCriterionMatch(data, 2)?.explanation ?? "-",
  },
  {
    name: "criterionMatch_resume",
    title: 'Критерий "Оформление резюме"',
    filterName: "criterionMatch_resume",
    render: (data) => getCriterionMatch(data, 3)?.score ?? "-",
  },
  {
    name: "criteria_resume_text",
    title: 'Критерий "Оформление резюме" (комментарий)',
    render: (data) => getCriterionMatch(data, 3)?.explanation ?? "-",
  },
  {
    name: "criterionMatch_projects",
    title: 'Критерий "Проекты и достижения"',
    filterName: "criterionMatch_projects",
    render: (data) => getCriterionMatch(data, 4)?.score ?? "-",
  },
  {
    name: "criteria_projects_text",
    title: 'Критерий "Проекты и достижения" (комментарий)',
    render: (data) => getCriterionMatch(data, 4)?.explanation ?? "-",
  },
  {
    name: "criterionMatch_soft_skills",
    title: 'Критерий "Софт-скиллы"',
    filterName: "criterionMatch_soft_skills",
    render: (data) => getCriterionMatch(data, 5)?.score ?? "-",
  },
  {
    name: "criteria_soft_skills_text",
    title: 'Критерий "Софт-скиллы" (комментарий)',
    render: (data) => getCriterionMatch(data, 5)?.explanation ?? "-",
  },
  {
    name: "advantages",
    title: "Ключевые плюсы кандидата",
    cellStyle: { whiteSpace: "pre-wrap" },
    render: (data) => data.currentCvCheck?.assessmentCV?.keyPoints.advantages.join(".\n\n") || "-",
  },
  {
    name: "disadvantages",
    title: "Ключевые минусы кандидата",
    cellStyle: { whiteSpace: "pre-wrap" },
    render: (data) => data.currentCvCheck?.assessmentCV?.keyPoints.disadvantages.join(".\n\n") || "-",
  },
  {
    name: "recommendations",
    title: "Рекомендации",
    cellStyle: { whiteSpace: "pre-wrap" },
    render: (data) => data.currentCvCheck?.assessmentCV?.keyPoints.recommendations.join(".\n\n") || "-",
  },
];

export default getColumnConfig;
